import { OrganizationService } from './../organizations/service/organization.service';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, SimpleChange, SimpleChanges } from '@angular/core';
import { Grade } from 'src/app/model/school.model';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Observable, forkJoin, map, throwError, catchError, of } from 'rxjs';

@Component({
    selector: 'grade-dropdown',
    templateUrl: './grade-dropdown.component.html',
    styleUrls: ['./grade-dropdown.component.scss']
})
export class GradeDropdownComponent implements OnInit {
    @Input() label: string = '';
    @Input() isMultiple: boolean = false;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() disabledGrades: string[] = [];

    @Input('reset') reset: boolean;
    @Output() gradeEvent = new EventEmitter<Grade[]>();
    @Input() chosenGrades: Grade[] = [];
    @Output() chosenGradesChange: EventEmitter<Grade[]> = new EventEmitter<Grade[]>();

    grades: Grade[] = [];
    gradesFlat: string;

    form: UntypedFormGroup;
    @ViewChild(MatSelect) matSelect: MatSelect;

    gradeOptions = [
        { INDEX: 1, GRADES: 'P0', selectable: true },
        { INDEX: 2, GRADES: 'P1', selectable: true },
        { INDEX: 3, GRADES: 'P2', selectable: true },
        { INDEX: 4, GRADES: 'P3', selectable: true },
        { INDEX: 5, GRADES: 'P4', selectable: true },
        { INDEX: 6, GRADES: 'P5', selectable: true },
        { INDEX: 7, GRADES: 'P6', selectable: true },
        { INDEX: 8, GRADES: 'PK', selectable: true, requires: ['R'] },
        { INDEX: 9, GRADES: 'TK', selectable: true, requires: ['R'] },
        { INDEX: 10, GRADES: 'K', selectable: true, requires: ['R'] },
        { INDEX: 11, GRADES: '01', selectable: true, requires: ['R'] },
        { INDEX: 12, GRADES: '02', selectable: true, requires: ['R'] },
        { INDEX: 13, GRADES: '03', selectable: true, requires: ['R'] },
        { INDEX: 14, GRADES: '04', selectable: true, requires: ['R'] },
        { INDEX: 15, GRADES: '05', selectable: true, requires: ['R'] },
        { INDEX: 16, GRADES: '06', selectable: true, requires: ['R'] },
        { INDEX: 17, GRADES: '07', selectable: true, requires: ['R'] },
        { INDEX: 18, GRADES: '08', selectable: true, requires: ['R'] },
        { INDEX: 19, GRADES: '09', selectable: true, requires: ['R'] },
        { INDEX: 20, GRADES: '10', selectable: true, requires: ['R'] },
        { INDEX: 21, GRADES: '11', selectable: true, requires: ['R'] },
        { INDEX: 22, GRADES: '12', selectable: true, requires: ['R'] },
        { INDEX: 23, GRADES: 'UG', selectable: true, requires: ['R'], static: true },
        { INDEX: 24, GRADES: 'AD', selectable: true, requires: ['R'] },
        { INDEX: 25, GRADES: 'NA', selectable: true, requires: ['N'], static: true }
    ];

    constructor(
        private organizationService: OrganizationService,
        private fb: UntypedFormBuilder
    ) {
        this.form = this.fb.group({
            gradeSelect: [[]]
        });
        this.getGrades();
    }

    ngOnInit(): void {
        let obs: Observable<any>[] = [];
        obs.push(this.getGrades());

        forkJoin(obs).subscribe((res) => {
            // Set form fields
            this.gradeSelect.valueChanges.subscribe((grades: Grade[]) => {
                this.gradeEvent.emit(grades);
            });

            if (this.chosenGrades) {
                let output: Grade[] = [];

                this.chosenGrades.forEach((obj: any) => {
                    let findGrade: any = this.grades.find((grade: Grade) => grade.ID === obj.ID || grade.GRADES === obj.GRADES);

                    if (findGrade) {
                        output.push(findGrade);
                    }
                });

                this.chosenGrades = output;
                this.gradeSelect.patchValue(this.chosenGrades);
                this.chosenGradesChange.emit(this.chosenGrades);
                this.updateGradesFlat();
            }

            if (this.disabled) {
                this.form.disable();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.reset) {
            this.resetFunction();
        }
        if (this.disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }

        if (changes['chosenGrades']) {
            if (!this.compareChange(changes['chosenGrades']['currentValue'], changes['chosenGrades']['previousValue'])) {
                let currentValue = changes['chosenGrades'].currentValue;

                let grades: Grade[] = [];
                currentValue.forEach((grade: Grade) => {
                    let foundGrade = this.grades.find((g) => g.ID === grade.ID || g.GRADES === grade.GRADES);
                    if (foundGrade) {
                        grades.push(foundGrade);
                    }
                });
                grades = grades.sort((a: any, b: any) => (a.INDEX! > b.INDEX! ? 1 : -1));
                if (!this.isMultiple) {
                    this.gradeSelect.patchValue(grades[0]); // Required for single select to display correctly, output will still be an array
                } else {
                    this.gradeSelect.patchValue(grades);
                }

                this.updateGradesFlat();
            }
        }
    }

    getGrades() {
        return this.organizationService.getGradesTable().pipe(
            map((res: any) => {
                if (res.grades) {
                    this.grades = this.combineGrades(res.grades);
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Grades.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    combineGrades(grades: Grade[]): Grade[] {
        let output: any[] = [];

        this.gradeOptions.forEach((obj: any) => {
            let findGrade = grades.find((grade: Grade) => grade.GRADES === obj.GRADES);

            output.push({ ...obj, ...findGrade });
        });

        if (this.disabledGrades) {
            this.disabledGrades.forEach((dg) => {
                let findGradeIndex = grades.findIndex((grade: Grade) => grade.GRADES === dg);

                if (findGradeIndex >= 0) {
                    output[findGradeIndex].ACTIVE = false;
                }
            });
        }

        return output;
    }

    get gradeSelect(): AbstractControl {
        return this.form.get('gradeSelect')!;
    }

    selectAll() {
        if (this.isMultiple) {
            this.gradeSelect.patchValue(this.grades);
            this.chosenGrades = JSON.parse(JSON.stringify(this.grades));
            this.chosenGradesChange.emit(this.chosenGrades);
        }
    }

    deSelectAll() {
        this.reset = false;
        this.gradeSelect.patchValue([]);
        this.chosenGrades = [];
        this.chosenGradesChange.emit([]);

        if (!this.isMultiple) {
            this.close();
        }
    }

    close() {
        this.matSelect.close();
    }

    resetFunction() {
        this.gradeSelect.setValue([]);
        this.gradeEvent.emit([]);
        this.chosenGradesChange.emit([]);
        this.reset = false;
    }

    selectGrade(grades: Grade[]) {
        if (!Array.isArray(grades)) {
            grades = [grades];
        }
        this.chosenGrades = grades;
        this.chosenGradesChange.emit(this.chosenGrades);
    }

    updateGradesFlat() {
        if (this.chosenGrades) {
            let output: Grade[] = [];
            this.gradeOptions.forEach((obj: any) => {
                let findGrade = this.chosenGrades.find((grade: Grade) => grade.GRADES === obj.GRADES);

                if (findGrade) {
                    output.push({ ...obj, ...findGrade });
                }
            });

            this.gradesFlat = output
                .sort((a: any, b: any) => (a.INDEX! > b.INDEX! ? 1 : -1))
                .map((g: Grade) => g.GRADES)
                .join(', ');
        } else {
            this.gradesFlat = '';
        }
    }

    compareChange(array1: any[], array2: any[]) {
        if (array2) {
            const array2Sorted = array2.slice().sort();
            return (
                array1.length === array2.length &&
                array1
                    .slice()
                    .sort()
                    .every(function (value, index) {
                        return value === array2Sorted[index];
                    })
            );
        } else {
            return false;
        }
    }
}
